import Placeholder from 'assets/img/covers/placeholder.jpg';
import ProfilePlaceholder from 'assets/img/profile_placeholder.png';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { FiShoppingBag } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { formatPrice } from 'utils/products';
import * as Ui from './LookCustomerPreview.styles';

const MOBILE_PRODUCTS_PER_PAGE = 2;

const getFirstVariant = (product) => {
  const firstChild = product?.child_products?.[0];
  return firstChild?.child_product || firstChild;
};

const PreviewMedia = ({ alt, media, mediaType }) =>
  mediaType === 'video' ? (
    <Ui.PreviewVideo src={media} aria-label={alt} autoPlay loop muted playsInline />
  ) : (
    <Ui.PreviewImage src={media || Placeholder} alt={alt} />
  );

PreviewMedia.propTypes = {
  alt: PropTypes.string.isRequired,
  media: PropTypes.string,
  mediaType: PropTypes.oneOf(['image', 'video']),
};

const LookProductRow = ({ product }) => {
  const { t } = useTranslation('app');
  const variant = getFirstVariant(product);
  const productName = variant?.parent_product?.name || product?.name;
  const hasPrice = variant?.price !== undefined && variant?.price !== null && variant?.currency;

  if (!variant) return null;

  return (
    <Ui.LookProductRow>
      <Ui.LookProductImage src={variant.picture || Placeholder} alt="" aria-hidden="true" />
      <Ui.LookProductDetails>
        <Ui.LookProductName>{productName || t('mediaCrop.productNameFallback')}</Ui.LookProductName>
        <Ui.VariantControls>
          {variant.size?.name && <Ui.SizeValue>{variant.size.name}</Ui.SizeValue>}
          {variant.color && (
            <Ui.ColorValue
              $color={variant.color.hex_value || 'transparent'}
              aria-label={variant.color.name}
              title={variant.color.name}
            />
          )}
        </Ui.VariantControls>
      </Ui.LookProductDetails>
      <Ui.LookProductActions>
        <Ui.BasketButton type="button" disabled aria-label={t('mediaCrop.basketPreview')}>
          <FiShoppingBag aria-hidden="true" />
        </Ui.BasketButton>
        {hasPrice && (
          <Ui.LookProductPrice>{formatPrice(variant.price, variant.currency)}</Ui.LookProductPrice>
        )}
      </Ui.LookProductActions>
    </Ui.LookProductRow>
  );
};

LookProductRow.propTypes = {
  product: PropTypes.object.isRequired,
};

const LookCustomerPreview = ({
  description,
  media,
  mediaType = 'image',
  name,
  originImage,
  products = [],
}) => {
  const { t } = useTranslation('app');
  const [productPage, setProductPage] = useState(0);
  const validProducts = products.filter((product) => getFirstVariant(product));
  const productPagesCount = Math.ceil(validProducts.length / MOBILE_PRODUCTS_PER_PAGE);
  const pageStart = productPage * MOBILE_PRODUCTS_PER_PAGE;
  const pageEnd = Math.min(pageStart + MOBILE_PRODUCTS_PER_PAGE, validProducts.length);
  const previewName = name?.trim() || t('mediaCrop.lookNameFallback');
  const previewDescription = description?.trim() || t('mediaCrop.lookDescriptionFallback');

  useEffect(() => {
    setProductPage((current) => Math.min(current, Math.max(productPagesCount - 1, 0)));
  }, [productPagesCount]);

  return (
    <Ui.ResponsivePreview>
      <Ui.LookCard>
        <Ui.LookMediaFrame>
          <PreviewMedia alt={previewName} media={media} mediaType={mediaType} />
          <Ui.OriginImageFrame>
            <Ui.OriginImage src={originImage || ProfilePlaceholder} alt="" />
          </Ui.OriginImageFrame>
        </Ui.LookMediaFrame>
        <Ui.LookContent>
          <Ui.LookHeader>
            <Ui.LookName>{previewName}</Ui.LookName>
            <Ui.LookDescription>{previewDescription}</Ui.LookDescription>
          </Ui.LookHeader>
          <Ui.ProductPanel>
            {validProducts.length === 0 ? (
              <Ui.EmptyProducts role="status">
                <Ui.EmptyProductsTitle>{t('mediaCrop.noProductsTitle')}</Ui.EmptyProductsTitle>
                <Ui.EmptyProductsText>{t('mediaCrop.noProductsMessage')}</Ui.EmptyProductsText>
              </Ui.EmptyProducts>
            ) : (
              <>
                <Ui.ProductList>
                  {validProducts.map((product, index) => (
                    <Ui.ProductListItem
                      key={product.uid || getFirstVariant(product).uid}
                      $mobileVisible={index >= pageStart && index < pageEnd}
                    >
                      <LookProductRow product={product} />
                    </Ui.ProductListItem>
                  ))}
                </Ui.ProductList>
                {productPagesCount > 1 && (
                  <Ui.ProductPagination
                    aria-label={t('mediaCrop.productPagination', { look: previewName })}
                  >
                    <Ui.ProductPageButton
                      type="button"
                      disabled={productPage === 0}
                      aria-label={t('mediaCrop.previousProducts')}
                      onClick={() => setProductPage((current) => Math.max(current - 1, 0))}
                    >
                      <FaAngleLeft aria-hidden="true" />
                    </Ui.ProductPageButton>
                    <Ui.ItemRange aria-live="polite">
                      {t('mediaCrop.itemRange', {
                        start: pageStart + 1,
                        end: pageEnd,
                        total: validProducts.length,
                      })}
                    </Ui.ItemRange>
                    <Ui.ProductPageButton
                      type="button"
                      disabled={productPage === productPagesCount - 1}
                      aria-label={t('mediaCrop.nextProducts')}
                      onClick={() =>
                        setProductPage((current) => Math.min(current + 1, productPagesCount - 1))
                      }
                    >
                      <FaAngleRight aria-hidden="true" />
                    </Ui.ProductPageButton>
                  </Ui.ProductPagination>
                )}
              </>
            )}
          </Ui.ProductPanel>
        </Ui.LookContent>
      </Ui.LookCard>
    </Ui.ResponsivePreview>
  );
};

LookCustomerPreview.propTypes = {
  description: PropTypes.string,
  media: PropTypes.string,
  mediaType: PropTypes.oneOf(['image', 'video']),
  name: PropTypes.string,
  originImage: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.object),
};

export default LookCustomerPreview;
